import * as React from 'react';
import { createRoot } from 'react-dom/client';
import {
    displayAbstractMediaCenterWishlistIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterWishlistIcon';

import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import { EProductDetailPageEvents } from 'PyzShopUi/scripts/product-detail-page/events/pdpEvent';
import { Documents, Document } from 'PyzShopUi/scripts/product-detail-page/components/Documents';
import {
    displayMediaCenterSoundIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterSoundIcon';
import {
    displayMediaCenterVideoIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterVideoIcon';
import ProductReviewHandler from 'PyzShopUi/scripts/product-review/ProductReviewHandler';
import Component from 'ShopUi/models/component';
import ProductConfiguratorHandler from 'PyzShopUi/scripts/product-detail-page/includes/product-configurator/ProductConfiguratorHandler';
import ArticleTabVariants from '../../molecules/article-tab-variants/article-tab-variants';
import DocumentContainer from 'PyzShopUi/scripts/utils/DocumentContainer';
import Loading from 'PyzShopUi/scripts/react/common/components/Loading';
import EtrackerTracking from 'PyzShopUi/scripts/etracker/EtrackerTracking';
import { IEtrackerProduct } from 'PyzShopUi/scripts/etracker/IEtrackerTracking';
import Utils from 'PyzShopUi/scripts/utils/utils';

interface Mediaset {
    name: string;
    images: Document[];
}

class ProductDetailPageHandler extends Component {
    protected readyCallback(): void {
    }

    private apiUrlElementRoot;

    init = (): void => {
        const adpWrapper: HTMLElement = document.querySelector('.article-main-info');
        if (!adpWrapper) {
            return;
        }

        this.trackProductDetailPage(adpWrapper);
        displayAbstractMediaCenterWishlistIcon();

        EventBus.getInstance().subscribe(EProductDetailPageEvents.LOADED_TAB_ALL_VARIANTS, () => {
            this.loadProductConfigurator(adpWrapper);
            this.loadReviewsTab();
        });

        this.loadAllVariantsTab();
        this.loadDocumentsTab();

    }

    private loadProductConfigurator(adpWrapper: HTMLElement): void {
        ProductConfiguratorHandler.init(adpWrapper);
    }

    private loadAllVariantsTab() {

        const apiUrlElement: HTMLElement = document.querySelector('[data-product-all-variants-api-url]');
        const apiUrl = apiUrlElement.dataset.productAllVariantsApiUrl;
        const apiUrlObject = new URL(apiUrl, window.location.origin);
        const addToCartApiUrl = apiUrlElement.dataset.addToCartApiUrl;
        const recommendationApiUrl = apiUrlElement.dataset.recommendationApiUrl;
        const variantListInfoElement: HTMLElement = document.querySelector('[data-variant-list]');
        if (!this.apiUrlElementRoot) {
            this.apiUrlElementRoot = createRoot(apiUrlElement);
        }
        const tabVariantsRender = data => {
            this.apiUrlElementRoot.render(
                <React.Suspense fallback={<Loading />}>
                    <ArticleTabVariants
                        data={data}
                        addToCartApiUrl={addToCartApiUrl}
                        recommendationApiUrl={recommendationApiUrl}
                    />
                </React.Suspense>
            );
        };

        if (variantListInfoElement) { // sap
            const data = JSON.parse(variantListInfoElement?.dataset.variantList);
            tabVariantsRender(data);
        } else { // i5
            fetch(apiUrlObject.toString())
                .then((response: Response) => response.json())
                .then(data => {
                    tabVariantsRender(data);
                })
                .catch((fetchCatchError: Error) => {
                    // error handling
                });
        }
    }

    private loadDocumentsTab() {
        const apiUrlElement: HTMLElement = document.querySelector('[data-product-documents-api-url]');
        const apiUrl = apiUrlElement.dataset.productDocumentsApiUrl;
        const initialDocuments = parseInt(apiUrlElement.dataset.initialDocuments);
        const apiUrlObject = new URL(apiUrl, window.location.origin);

        if (initialDocuments) {
            fetch(apiUrlObject.toString())
                .then((response: Response) => response.json())
                .then(data => {
                    const container = document.querySelector('.article-documents');
                    const containerList = document.querySelector('.article-document-list');
                    if (container && !containerList) {
                        const filterMediaByType = (mediaSets: Mediaset[], type: string): Document[] => mediaSets
                            .filter(mediaSet => mediaSet.name === type)
                            .reduce((result, mediaSet) => {
                                result.push(...mediaSet.images);
                                // sort documents alphabetically
                                result.sort((firstDocument, secondDocument) => {
                                    return firstDocument?.title.localeCompare(secondDocument?.title);
                                });

                                return result;
                            }, []);

                        const certificates = filterMediaByType(data, 'Certificate');
                        const manuals = filterMediaByType(data, 'Manual');
                        const safetyDataSheets = filterMediaByType(data, 'SafetyDataSheet');
                        const urls = filterMediaByType(data, 'Link');
                        const sounds = filterMediaByType(data, 'Sound');
                        const videos = filterMediaByType(data, 'Video');

                        if (sounds) {
                            displayMediaCenterSoundIcon(data);
                        }
                        if (videos) {
                            displayMediaCenterVideoIcon(data);
                        }

                        const documentRoot = DocumentContainer.createDocumentContainer(container);

                        const anl = filterMediaByType(data, 'ANL');
                        const sic = filterMediaByType(data, 'SIC');
                        const snd = filterMediaByType(data, 'SND');
                        const tuv = filterMediaByType(data, 'TUV');
                        const link = filterMediaByType(data, 'Link');

                        const pimsSafetyDataSheets = safetyDataSheets.concat(sic);
                        const pimsManuals = manuals.concat(anl);
                        const pimsCertificates = certificates.concat(tuv);
                        const pimsSounds = sounds.concat(snd);
                        const pimsUrls = urls.concat(link);

                        documentRoot.render(
                            <React.Suspense fallback={<Loading/>}>
                                <Documents manuals={pimsManuals}
                                           safetyDataSheets={pimsSafetyDataSheets}
                                           certificates={pimsCertificates}
                                           sounds={pimsSounds}
                                           urls={pimsUrls}/>
                            </React.Suspense>
                        );
                    }
                })
                .catch((fetchCatchError: Error) => {
                    // error handling
                });
        }
    }

    private readonly loadReviewsTab = () => {
        ProductReviewHandler.init();
    }

    private trackProductDetailPage(adpWrapper: HTMLElement): void {
        const productResponseElement: HTMLElement = adpWrapper.querySelector('[data-product-client-response]');
        const productResponse = JSON.parse(productResponseElement.dataset.productClientResponse);

        const etrackerProduct: IEtrackerProduct = {
            id: productResponse.sku,
            name: productResponse.seo_title,
            price: Utils.formatPrice(productResponse.current_product_price.price),
            currency: productResponse.current_product_price.currency.code,
        };

        const categoryName = document.querySelector('#last-category')?.getAttribute('data-category-name') ?? '';
        if (categoryName !== '') {
            etrackerProduct.category = [categoryName];
        }

        EtrackerTracking.viewProduct(etrackerProduct);
    }
}

// tslint:disable-next-line:max-file-line-count
export default ProductDetailPageHandler;
